/**
 * MySkillsPage: the signed-in user's own plugin records (epic #514 / Work Item C).
 *
 * Backed by the authenticated `GET /skill/my-records` endpoint (Contract 5), NOT
 * marketplace.json: that catalog is active-only and carries no `status` or
 * `refusalReason`, both of which this page must surface. Each row is a
 * `MyRecordEntry` (deliberately NOT a Contract-3 `MarketplaceEntry`), so we do
 * not reuse PluginCard/PluginListView here; a refused/sparse row lacks the rich
 * fields those read (pluginName/description/categories/etc.) and has nowhere to
 * show status. The row is rendered natively, its display branching on `status`.
 *
 * Records are grouped by `ownership`:
 *   - "Published by you"  — verified-contributor rows (web-uploaded). Edit + Delete.
 *   - "Owned via GitHub"  — self-asserted rows (author-email match). Read-only in v1.
 *   - "Possibly yours"    — tier-2 GitHub-identity matches. Always empty in v1.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { MyRecordEntry } from "@skills-marketplace/contracts/contract-5-github-proxy";
import type { MarketplaceEntry } from "../types";
import {
  MAX_FAMILY_ENTRIES,
  type ContributorFeedbackGroup,
} from "@skills-marketplace/contracts/contract-8-feedback";
import {
  getMyRecords,
  deleteSkill,
  GitHubProxyError,
  NetworkBlockedError,
} from "../lib/github-proxy-client";
import { getReceivedFeedback } from "../lib/feedback-client";
import { authorUsefulnessHeadline } from "../lib/feedback";
import { onSpaLinkClick } from "../lib/link-click";

/**
 * The #621 A3 disclosure: feedback (below, in the same section) is the first
 * sensitive thing keyed on this unverified signal — ownership here is the
 * manifest's author email, not a verified login — so say so plainly.
 *
 * TODO(#621 A3): wording pending product/Security sign-off; structure is final.
 * Exported as one constant (#621 review) rather than inlined so a reword is a
 * one-line change here — MySkillsPage.test.tsx still pins the substring "hasn't
 * been verified against your login" regardless of where the string lives.
 */
export const UNVERIFIED_OWNERSHIP_NOTE =
  "Ownership is based on the manifest's author email and hasn't been verified against your login.";
import FeedbackReasons from "../components/FeedbackReasons";
import { reportError, reportEvent } from "../lib/newrelic";
import EmptyState from "../components/EmptyState";
import StatusBadge from "../components/StatusBadge";
import SourceBadge from "../components/SourceBadge";
import Loading from "../components/Loading";
import InfoIcon from "../icons/InfoIcon";
import LockIcon from "../icons/LockIcon";
import { useCatalog, detailHrefForInstallKey } from "../lib/catalog-context";
import styles from "./MySkillsPage.module.css";

interface Props {
  /**
   * Open the catalog detail page for a record. Only ever called with a present
   * `installName` (a deep-linkable, active+listed row), so the caller can resolve
   * it against the loaded marketplace.json.
   */
  onOpenDetail: (installName: string) => void;
  /**
   * Open the edit page for an editable record. Called with the record's
   * `installName` (the key the edit route resolves against the catalog).
   */
  onEdit: (installName: string) => void;
  /**
   * A record was deleted. Called with its `installName` so the caller can drop
   * it from the catalog context (`removePlugin`). Only fired when `installName`
   * is present (an inactive row isn't in the catalog to begin with).
   */
  onRecordDeleted: (installName: string) => void;
  /** Navigate to the contribute page (whole-page empty-state CTA). */
  onContribute: () => void;
  /** Navigate to the help page (rejection-box eligibility link). */
  onHelp: () => void;
}

// An active record can flip to inactive with no refusalReason via a repo
// lifecycle event or a removed manifest. There is no stored per-record cause, so
// the info tip lists the possibilities rather than asserting one.
const DELIST_REASONS =
  "This usually means the repository was archived, deleted, renamed, or made private, or the plugin manifest was removed from it.";

function formatUpdated(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function skillCount(n: number): string {
  return `${n} skill${n === 1 ? "" : "s"}`;
}

/**
 * The summed vote tally across a plugin row and its synthesized skill entries (#621).
 * A row is one plugin record, but consumers vote per catalog entry — the plugin plus
 * one per embedded skill — so a plugin-only tally would read "no feedback" for a
 * multi-skill plugin whose skills are the things people actually voted on.
 *
 * The family is the entry's own `publishedSkillTwins`, which the Catalog Builder stamps
 * for exactly this purpose. See the loop below for why the link-target field cannot
 * stand in for it.
 *
 * Returns both fields for a reason:
 *   - `useful` drives the headline "N found useful" — a COUNT, never a summed ratio
 *     (a ratio would manufacture a rating Contract 8 forbids). Only the positive tally
 *     is ever shown (#617); 0 is a valid positive tally.
 *   - `total` decides whether there is ANY feedback to read. A plugin with only
 *     down-votes has `useful` 0 but `total` > 0, and its reasons are the actionable
 *     kind — so expand and the empty state key off `total`, not `useful`.
 *
 * Returns null when the row is not in the catalog (no installName ⇒ inactive/hidden/
 * refused): the caller renders nothing rather than a misleading zero. The family is
 * read from the catalog entry's own `skills[]`, since MyRecordEntry carries no skills.
 */
export function familyTally(
  installName: string | undefined,
  byInstallName: Map<string, MarketplaceEntry>,
): { useful: number; total: number } | null {
  if (!installName) return null;
  const entry = byInstallName.get(installName);
  if (!entry) return null;
  let useful = entry.feedback?.useful ?? 0;
  let total = entry.feedback?.total ?? 0;
  // The family comes from the catalog's own `publishedSkillTwins` (Contract 3 1.4), NOT
  // from `skills[].installName`. That field is a LINK target and the two sets differ in
  // both directions: it is absent for a skill sharing its parent's name (#329) even
  // though that twin is published and votable — which made a self-named skill's
  // feedback unreachable here, since a zero `total` hides the expand entirely — and it
  // can point at a DIFFERENT record's standalone entry (the same-repo suppressor),
  // whose votes belong to that record's own row. Reading the server-provided family
  // fixes both and keeps this identical to the set `/feedback/received` fans out over,
  // without the browser deriving a single key (#547, contract-8).
  for (const twin of entry.publishedSkillTwins ?? []) {
    const t = byInstallName.get(twin)?.feedback;
    useful += t?.useful ?? 0;
    total += t?.total ?? 0;
  }
  return { useful, total };
}

/**
 * A 200 whose body isn't the expected shape. A distinct type (not a plain Error)
 * so messageForError can surface its curated message while keeping raw lower-layer
 * strings (e.g. an MSAL diagnostic) out of the UI.
 */
class MalformedResponseError extends Error {}

/**
 * Map a fetch failure to user-facing copy. Never surface a raw lower-layer message
 * (handleResponse's "non-JSON response (401)", an MSAL AADSTS string): a 401/403 is
 * an auth problem the user must re-authenticate for, a NetworkBlockedError is a
 * connectivity/security-filter problem, and everything else is generic. The raw
 * error still goes to New Relic via reportError for diagnosis.
 */
function messageForError(err: unknown): string {
  if (err instanceof NetworkBlockedError) {
    return "We couldn't reach the server. Check your connection and try again.";
  }
  if (err instanceof GitHubProxyError && (err.statusCode === 401 || err.statusCode === 403)) {
    return "Your session may have expired, or you don't have access to this. Try signing in again, or contact IT Support if this keeps happening.";
  }
  if (err instanceof MalformedResponseError) {
    return err.message;
  }
  return "Something went wrong loading your skills. Please try again.";
}

interface MyRecordRowProps {
  entry: MyRecordEntry;
  onOpenDetail: Props["onOpenDetail"];
  onHelp: Props["onHelp"];
  /**
   * True only in the "Published by you" section: renders Edit (when deep-linkable)
   * and Delete controls. Requires onEdit + onDeleted.
   */
  canManage?: boolean;
  onEdit?: Props["onEdit"];
  onDeleted?: (entry: MyRecordEntry) => void;
  /** True for GitHub-sourced (read-only) rows: renders the lock note instead of controls. */
  readOnly?: boolean;
  /**
   * Summed vote tally across this plugin and its skills (#621), or null when the row
   * isn't in the catalog (nothing to count). See familyTally.
   */
  tally?: { useful: number; total: number } | null;
}

type FeedbackExpand =
  | { status: "idle" }
  | { status: "loading" }
  /**
   * `truncated` mirrors the response flag of the same name: the family hit
   * MAX_FAMILY_ENTRIES and the groups shown are a prefix, not the whole set — an
   * EXPECTED ceiling with a known cause.
   *
   * `partial` mirrors the response flag of the same name too, but is a DIFFERENT
   * signal (#621 review): one or more fan-out entries failed or timed out
   * server-side, so an entry that MIGHT have had feedback is simply missing from
   * `groups` rather than genuinely empty. Unlike `truncated`, this is unexpected —
   * the copy below offers a retry rather than stating a known limit.
   */
  | { status: "loaded"; groups: ContributorFeedbackGroup[]; truncated?: boolean; partial?: boolean }
  | { status: "error" };

function MyRecordRow({ entry, onOpenDetail, onHelp, canManage, onEdit, onDeleted, readOnly, tally }: MyRecordRowProps) {
  const { installKey, installName, displayName, orgOrOwner, repoName, status, hidden, refusalReason, updatedAt, ownership } = entry;
  const published = status === "active" && !hidden;
  const source = ownership === "verified-contributor" ? "web-upload" : "github";

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Feedback reasons are fetched lazily, once, on first expand (#621). `expand` is the
  // fetch cache (persists across hide/show); `open` is visibility. getReceivedFeedback
  // never throws — a null result becomes the "error" state with an inline retry, so one
  // row's failed fetch never blanks the dashboard.
  const [expand, setExpand] = useState<FeedbackExpand>({ status: "idle" });
  const [open, setOpen] = useState(false);

  async function loadFeedback() {
    if (!installName) return;
    setExpand({ status: "loading" });
    const res = await getReceivedFeedback(installName);
    if (!res) {
      // getReceivedFeedback never throws, so without this the failure is invisible to
      // telemetry — the contributor sees "Couldn't load feedback" and support has
      // nothing to search on. The client console-warns the specific cause (status code,
      // bad shape, schema version); this records that the row failed at all, with the
      // entry it failed for.
      reportError(new Error("getReceivedFeedback returned no result"), "my-records-feedback", {
        installName,
      });
      setExpand({ status: "error" });
      return;
    }
    setExpand({ status: "loaded", groups: res.groups, truncated: res.truncated, partial: res.partial });
  }

  function showFeedback() {
    setOpen(true);
    // Fetch only if we don't already hold a result (idle or a prior error/retry).
    if (expand.status === "idle" || expand.status === "error") void loadFeedback();
  }

  async function handleDelete() {
    // Mirrors PluginDetailPage.handleDelete: native confirm, then a proxy DELETE.
    // Click marker fires BEFORE confirm so even a cancel or a pre-fetch throw is
    // observable (see PluginDetailPage for the rationale).
    reportEvent("skillDeleteClicked", { installKey, surface: "my-skills" });
    if (!window.confirm(`Delete "${displayName}"? This cannot be undone.`)) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteSkill(installKey);
      // Parent removes this row from state (unmounting it) and drops it from the
      // catalog context; no post-success state set is needed here.
      onDeleted?.(entry);
    } catch (err) {
      reportError(err, "my-records-delete", { installKey });
      setDeleting(false);
      setDeleteError("We couldn't delete this skill. Please try again.");
    }
  }

  return (
    <li className={styles.row}>
      {installName ? (
        <a
          href={detailHrefForInstallKey(installName)}
          className={styles.titleLink}
          onClick={onSpaLinkClick(() => onOpenDetail(installName))}
        >
          {displayName}
        </a>
      ) : (
        <span className={styles.title}>{displayName}</span>
      )}
      <div className={styles.badges}>
        <SourceBadge source={source} />
        {published ? (
          <StatusBadge tone="success">Listed</StatusBadge>
        ) : status === "active" ? (
          <StatusBadge tone="warning">Hidden</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">Not listed</StatusBadge>
        )}
      </div>

      <p className={styles.meta}>
        {orgOrOwner}/{repoName} · Updated {formatUpdated(updatedAt)}
      </p>

      {/*
        Community feedback summary + expandable reasons (#621). Rendered only for a row
        that's in the catalog (tally non-null). The headline is authorUsefulnessHeadline's
        "N of M found this useful" — the full picture for the entry's own owner, never
        gated by the public rating threshold (#621 review) — so a down-vote-only plugin
        reads "0 of 4 found this useful" rather than a bare, context-free "0 found
        useful". Expand and the empty state key off `total`, so that same plugin still
        offers its reasons. Reasons load lazily on first expand and stay attributed per
        entry.
      */}
      {tally != null &&
        (tally.total > 0 ? (
          <div className={styles.feedback}>
            <p className={styles.feedbackCount}>{authorUsefulnessHeadline(tally.useful, tally.total)}</p>
            {open ? (
              <button
                type="button"
                className={styles.feedbackToggle}
                onClick={() => setOpen(false)}
              >
                Hide feedback
              </button>
            ) : (
              <button
                type="button"
                className={styles.feedbackToggle}
                onClick={showFeedback}
                disabled={expand.status === "loading"}
              >
                {expand.status === "loading" ? "Loading…" : "View feedback"}
              </button>
            )}
            {open && expand.status === "error" && (
              <p className={styles.feedbackError} role="alert">
                Couldn&apos;t load feedback.{" "}
                <button type="button" className={styles.feedbackRetry} onClick={loadFeedback}>
                  Retry
                </button>
              </p>
            )}
            {open &&
              expand.status === "loaded" &&
              (expand.groups.length > 0 ? (
                // installName is non-null whenever a loaded state exists: loadFeedback
                // returns early without one, and `tally != null` already requires it.
                <FeedbackReasons groups={expand.groups} parentInstallName={installName ?? ""} />
              ) : (
                <p className={styles.feedbackEmpty}>No written reasons left.</p>
              ))}
            {/*
              The backend caps the fan-out at MAX_FAMILY_ENTRIES and says so via
              `truncated`; say it here too, or a contributor with a very large plugin
              reads a partial list as the complete one.
            */}
            {open && expand.status === "loaded" && expand.truncated && (
              <p className={styles.feedbackTruncated}>
                Showing the first {MAX_FAMILY_ENTRIES} entries — some feedback may not be listed.
              </p>
            )}
            {/*
              `partial`: the backend dropped one or more entries it couldn't read
              (a timeout or a failed Query), NOT a page ceiling — say so distinctly
              from the `truncated` note above and offer the same retry the error
              state offers, since re-fetching might succeed where the flaky read
              didn't (#621 review).
            */}
            {open && expand.status === "loaded" && expand.partial && (
              <p className={styles.feedbackError} role="alert">
                Some feedback couldn&apos;t be loaded.{" "}
                <button type="button" className={styles.feedbackRetry} onClick={loadFeedback}>
                  Retry
                </button>
              </p>
            )}
          </div>
        ) : (
          <p className={styles.feedbackEmpty}>No feedback yet</p>
        ))}

      {/*
        Status line, driven by status. A published (active + visible) row gets no
        line: the "Listed" badge says it all. A hidden row needs a visible,
        actionable line (not a mouse-only tooltip) because the exclude topic is the
        one thing its owner can act on to relist it.
      */}
      {status === "active" && hidden && (
        <p className={styles.hiddenNote}>
          Hidden from the catalog. Remove the <code>plugin-marketplace-exclude</code> repository
          topic to list it again.
        </p>
      )}
      {status === "inactive" &&
        (refusalReason ? (
          <div className={styles.reasonBox}>
            <span className={styles.reasonLabel}>Rejection reason</span>
            {/* Author-controlled (contract-5): render as escaped text, never markup. */}
            <p className={styles.reasonText}>{refusalReason}</p>
            <button type="button" className={styles.reasonHelp} onClick={onHelp}>
              What makes a skill eligible?
            </button>
          </div>
        ) : (
          <p className={styles.noLongerListed}>
            <span>No longer listed.</span>
            <InfoIcon className={styles.infoIcon} label={DELIST_REASONS} />
          </p>
        ))}

      {canManage && (
        <div className={styles.actions}>
          {installName && (
            <button type="button" className={styles.editButton} onClick={() => onEdit?.(installName)}>
              Edit
            </button>
          )}
          <button
            type="button"
            className={styles.deleteButton}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
          {deleteError && (
            <p className={styles.deleteError} role="alert">
              {deleteError}
            </p>
          )}
        </div>
      )}

      {readOnly && (
        <p className={styles.lockNote}>
          <LockIcon className={styles.lockIcon} />
          Read-only. Edit and delete aren&apos;t available for GitHub-sourced skills in v1.
        </p>
      )}
    </li>
  );
}

export default function MySkillsPage({ onOpenDetail, onEdit, onRecordDeleted, onContribute, onHelp }: Props) {
  const [owned, setOwned] = useState<MyRecordEntry[]>([]);
  const [possiblyYours, setPossiblyYours] = useState<MyRecordEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  // Feedback counts are joined from the catalog context (#621): the bulk tallies are
  // already loaded there, keyed by installName, and only the catalog entry knows a
  // plugin's synthesized skill keys (MyRecordEntry doesn't carry skills[]). Built once
  // per plugins change; familyUsefulCount reads it per row.
  const { plugins } = useCatalog();
  const byInstallName = useMemo(() => {
    const map = new Map<string, MarketplaceEntry>();
    for (const entry of plugins) {
      if (entry.installName) map.set(entry.installName, entry);
    }
    return map;
  }, [plugins]);

  const handleDeleted = useCallback(
    (entry: MyRecordEntry) => {
      setOwned((prev) => prev.filter((e) => e.installKey !== entry.installKey));
      // Only listed rows are in the catalog context; an inactive/refused row has
      // no installName and nothing to remove there.
      if (entry.installName) onRecordDeleted(entry.installName);
    },
    [onRecordDeleted],
  );

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getMyRecords()
      .then((res) => {
        if (cancelled) return;
        // handleResponse only casts the parsed JSON to the response type; it
        // does not validate the shape. A 200 with an unexpected body would set
        // state to undefined and then throw in render, OUTSIDE this promise, so
        // it would escape the catch below and trip the app ErrorBoundary instead
        // of this page's own error/Retry. Treat a malformed success as an error
        // so the user gets Retry, not a misleading "no skills" empty state.
        if (!Array.isArray(res?.owned) || !Array.isArray(res?.possiblyYours)) {
          throw new MalformedResponseError("The server returned an unexpected response.");
        }
        setOwned(res.owned);
        setPossiblyYours(res.possiblyYours);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Keep the status on the telemetry so on-call can separate auth (401/403)
        // from backend 5xx from a network block, then show curated copy rather than
        // the raw lower-layer message.
        const status = err instanceof GitHubProxyError ? err.statusCode : undefined;
        if (status === undefined) {
          reportError(err, "my-records-fetch");
        } else {
          reportError(err, "my-records-fetch", { status });
        }
        setError(messageForError(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  if (isLoading) {
    return <Loading message="Loading your skills..." />;
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <h1>Couldn&apos;t load your skills</h1>
        <p className={styles.errorMessage}>{error}</p>
        <button type="button" onClick={retry} className={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  const published = owned.filter((e) => e.ownership === "verified-contributor");
  const gitHubOwned = owned.filter((e) => e.ownership === "self-asserted");
  const isEmpty = owned.length === 0 && possiblyYours.length === 0;

  return (
    <div>
      <div className={styles.header}>
        <h1>My Skills</h1>
        <p className={styles.subtitle}>
          Skills and plug-ins matched to your account, including ones that are no longer listed.
          Rejection reasons are shown only to you.
        </p>
      </div>

      {isEmpty ? (
        <EmptyState message="You haven't published any skills yet.">
          <p className={styles.emptyHint}>
            When you publish a skill or plug-in, by web upload or from a GitHub repo, it&apos;ll
            show up here with its status and any feedback.
          </p>
          <button type="button" className={styles.ctaButton} onClick={onContribute}>
            Publish your first skill
          </button>
        </EmptyState>
      ) : (
        <>
          <section aria-labelledby="published-heading" className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 id="published-heading" className={styles.sectionTitle}>Published by you</h2>
              <span className={styles.sectionCount}>{skillCount(published.length)}</span>
            </div>
            <p className={styles.sectionSub}>
              Skills you uploaded through the marketplace. You can edit or delete them.
            </p>
            {published.length === 0 ? (
              <p className={styles.sectionEmpty}>
                You haven&apos;t published any skills through the marketplace yet.
              </p>
            ) : (
              <ul className={styles.list}>
                {published.map((entry) => (
                  <MyRecordRow
                    key={entry.installKey}
                    entry={entry}
                    tally={familyTally(entry.installName, byInstallName)}
                    canManage
                    onOpenDetail={onOpenDetail}
                    onEdit={onEdit}
                    onDeleted={handleDeleted}
                    onHelp={onHelp}
                  />
                ))}
              </ul>
            )}
          </section>

          {gitHubOwned.length > 0 && (
            <section aria-labelledby="github-heading" className={styles.section}>
              <div className={styles.sectionHead}>
                <h2 id="github-heading" className={styles.sectionTitle}>Owned via GitHub</h2>
                <span className={styles.sectionCount}>{skillCount(gitHubOwned.length)}</span>
              </div>
              <p className={styles.sectionSub}>
                Skills from GitHub repos whose manifest author email matches yours. Status and
                any rejection reason are visible, but edit and delete stay web-upload-only in v1.
              </p>
              <p className={styles.unverifiedNote}>{UNVERIFIED_OWNERSHIP_NOTE}</p>
              <ul className={styles.list}>
                {gitHubOwned.map((entry) => (
                  <MyRecordRow
                    key={entry.installKey}
                    entry={entry}
                    tally={familyTally(entry.installName, byInstallName)}
                    readOnly
                    onOpenDetail={onOpenDetail}
                    onHelp={onHelp}
                  />
                ))}
              </ul>
            </section>
          )}

          <section aria-labelledby="possibly-heading" className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 id="possibly-heading" className={styles.sectionTitle}>Possibly yours</h2>
              <span className={styles.sectionCount}>{skillCount(possiblyYours.length)}</span>
            </div>
            <p className={styles.sectionSub}>
              Records that may belong to you based on your GitHub identity. Matching by GitHub
              login isn&apos;t available yet
              {possiblyYours.length === 0 ? ", so this list is empty for now." : "."}
            </p>
            {possiblyYours.length === 0 ? (
              <p className={styles.sectionEmpty}>Nothing here yet.</p>
            ) : (
              <ul className={styles.list}>
                {possiblyYours.map((entry) => (
                  <MyRecordRow
                    key={entry.installKey}
                    entry={entry}
                    tally={familyTally(entry.installName, byInstallName)}
                    readOnly
                    onOpenDetail={onOpenDetail}
                    onHelp={onHelp}
                  />
                ))}
              </ul>
            )}
          </section>

          <p className={styles.tierNote}>
            GitHub skills that match neither your manifest author email nor your GitHub push
            identity don&apos;t appear here.
          </p>
        </>
      )}
    </div>
  );
}
